"use client";

import { type ReactNode } from "react";
import { useClientSidebar } from "./client-sidebar-context";
import { NavFlyoutHost } from "./nav-flyout";

interface SidebarShellProps {
  nav: ReactNode;
}

const svgProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true as const,
};

/**
 * The icon rail. Navigation only since the top bar took over branding, the
 * company switcher, demo mode, and the account block (2026-08-01). It hangs
 * below the bar, so it sticks at the bar's height rather than the viewport top.
 */
export function SidebarShell({ nav }: SidebarShellProps) {
  const { collapsed, setCollapsed } = useClientSidebar();

  // No right border on the rail: the content panel's own light fill draws that
  // edge now, and a border would cut across its rounded corner.
  return (
    <aside
      className={`sticky top-[var(--topbar-h)] hidden h-[calc(100vh-var(--topbar-h))] shrink-0 flex-col bg-spark-black py-3 transition-all duration-200 lg:flex ${
        collapsed ? "w-14 px-2" : "w-56 px-4"
      }`}
    >
      <NavFlyoutHost>{nav}</NavFlyoutHost>

      <button
        onClick={() => setCollapsed(!collapsed)}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className={`mt-3 flex h-9 items-center gap-2 rounded-lg text-xs font-medium text-[#AAB2BD] transition-colors hover:bg-white/[.06] hover:text-white ${
          collapsed ? "mx-auto w-9 justify-center" : "w-full px-3"
        }`}
      >
        <svg {...svgProps}>
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <line x1="9" y1="3" x2="9" y2="21" />
          <polyline points={collapsed ? "13 9 16 12 13 15" : "14 9 11 12 14 15"} />
        </svg>
        {!collapsed && "Collapse"}
      </button>

      {/* The content panel's rounded top-left corner. Drawn here rather than as
          a radius on <main>: main scrolls, so its own corner slides up under
          the top bar and the curve disappears. The rail is sticky, so hanging
          the corner off its top-right edge keeps the curve on screen at every
          scroll position, and left-full tracks the collapse width for free.
          Self-contained on purpose (it paints its own dark wedge over the page
          background) so no ancestor needs a screen-only dark fill, which would
          otherwise have to be undone for print. */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-full top-0 h-3 w-3 bg-spark-black print:hidden"
      >
        <span className="block h-full w-full rounded-tl-xl bg-background" />
      </span>
    </aside>
  );
}
